'use client';

import { useState, useRef, useEffect } from 'react';

interface AdmissionPhaseData {
  phase: string;
  phaseLocal: string;
  dateStart: string;
  dateEnd: string;
}

interface Props {
  phases: AdmissionPhaseData[];
  pathway: string;
  year: number;
  lang: string;
}

function icsDate(dateStr: string): string {
  return dateStr.replace(/-/g, '');
}

function icsEndDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function escapeIcs(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

function buildICS(phases: AdmissionPhaseData[], pathway: string, lang: string): string {
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const isId = lang === 'id';

  const vevents = phases.map((p, i) => {
    const summary = isId ? `${pathway}: ${p.phaseLocal}` : `${pathway}: ${p.phase}`;
    return [
      'BEGIN:VEVENT',
      `UID:admission-${pathway.toLowerCase().replace(/\s+/g, '-')}-${i}@vacant.day`,
      `DTSTAMP:${now}`,
      `DTSTART;VALUE=DATE:${icsDate(p.dateStart)}`,
      `DTEND;VALUE=DATE:${icsEndDate(p.dateEnd)}`,
      `SUMMARY:${escapeIcs(summary)}`,
      `DESCRIPTION:${escapeIcs(isId ? `Jadwal ${pathway}` : `${pathway} schedule`)}`,
      'END:VEVENT',
    ].join('\r\n');
  });

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//vacant.day//Admission Calendar//EN',
    `X-WR-CALNAME:${escapeIcs(`${pathway} Schedule`)}`,
    ...vevents,
    'END:VCALENDAR',
  ].join('\r\n');
}

function buildGoogleCalendarUrl(phase: AdmissionPhaseData, pathway: string, lang: string): string {
  const isId = lang === 'id';
  const title = isId ? `${pathway}: ${phase.phaseLocal}` : `${pathway}: ${phase.phase}`;
  const start = icsDate(phase.dateStart);
  const end = icsEndDate(phase.dateEnd);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${start}/${end}`,
    details: isId ? `Jadwal ${pathway} — vacant.day` : `${pathway} schedule — vacant.day`,
  });

  return `https://www.google.com/calendar/render?${params.toString()}`;
}

export function AddToCalendarAdmission({ phases, pathway, year, lang }: Props) {
  const isId = lang === 'id';
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Find the next relevant phase for Google Calendar link
  const announcementPhase = phases.find(
    p => p.phase.toLowerCase().includes('announcement') || p.phaseLocal.toLowerCase().includes('pengumuman'),
  ) || phases[0];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleICS = () => {
    const ics = buildICS(phases, pathway, lang);
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pathway.toLowerCase().replace(/\s+/g, '-')}-${year}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setOpen(false);
    window._paq?.push(['trackEvent', 'Admission', 'Calendar-ICS', pathway]);
  };

  const handleGoogle = () => {
    window.open(buildGoogleCalendarUrl(announcementPhase, pathway, lang), '_blank', 'noopener');
    setOpen(false);
    window._paq?.push(['trackEvent', 'Admission', 'Calendar-Google', pathway]);
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 content-card px-3 py-2 text-xs font-medium text-text-secondary transition-colors hover:border-accent-primary hover:text-accent-primary"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clipRule="evenodd" />
        </svg>
        {isId ? 'Simpan ke Kalender' : 'Add to Calendar'}
      </button>
      {open && (
        <div className="absolute start-0 top-full z-10 mt-1 w-52 content-card py-1 shadow-lg">
          <button
            onClick={handleICS}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-secondary"
          >
            <svg className="h-4 w-4 text-text-tertiary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5z" clipRule="evenodd" />
            </svg>
            {isId ? 'Download .ICS' : 'Download .ICS'}
          </button>
          <button
            onClick={handleGoogle}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-secondary"
          >
            <svg className="h-4 w-4 text-text-tertiary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2z" clipRule="evenodd" />
            </svg>
            Google Calendar
          </button>
        </div>
      )}
    </div>
  );
}
